import React, { createContext, useState } from "react";
import axios from "axios";
import { API_BASE_URL } from "@config/config";
import { useMovies } from "@queries/useMovies";

export const AuthContext = createContext({});

const AuthContextProvider = (props) => {
    const [userAuth, setUserAuth] = useState({
        user: null,
        token: "",
        isLoggedIn: null,
        requireOtp: false,
    });
    const [errorMessage, setErrorMessage] = useState(null);
    const [isSignupSubmitting, setIsSignupSubmitting] = useState(false);
    const [isLoginSubmitting, setIsLoginSubmitting] = useState(false);
    const [loginError, setLoginError] = useState(null);
    const [unverifiedEmailToken, setUnverifiedEmailToken] = useState(null);
    const [letsStartLoginError, setLetsStartLoginError] = useState(null);
    const [processing, setProcessing] = useState(false);
    const [successMessage, setSuccessMessage] = useState(null);
    const [tempAccountLogin, setTempAccountLogin] = useState(false);
    const [errorMessages, setErrorMessages] = useState({});
    const { user, token, isLoggedIn } = userAuth;
    const [memberId, setMemberId] = useState("");
    const [isReadytoCheckout, setIsReadyToCheckout] = useState(false);
    const [loggedIn, setLoggedIn] = useState(false);
    const [emailToken, setEmailToken] = useState(null);
    const [emailTokenExpired, setEmailTokenExpired] = useState(false);
    const [isEmailTokenExpiredLoading, setIsEmailTokenExpiredLoading] = useState(false);

    const [pendingActionUrl, setPendingActionUrl] = useState(null);
    // Stripe Embedded Checkout session, populated by the initialise-stripe calls in
    // BookSessionContext and consumed by StripeCheckoutDrawer.
    const [stripeClientSecret, setStripeClientSecret] = useState(null);
    const [stripeSessionId, setStripeSessionId] = useState(null);
    const [userCoords, setUserCoords] = useState(null);
    const [movieRedirect, setMovieRedirect] = useState({
        venueName: null,
        movieUrl: null
    });
    const [drawerState, setDrawerState] = useState({
        venuesDrawer: false,
        venueSelectorDrawer: false,
        experienceDrawer: false,
        loginSignUpDrawer: false,
        complimentaryCheckoutDrawer: false,
        creditCardCheckoutDrawer: false,
        fastAuthDrawer: false,
        termsDrawer: false,
        stripeCheckoutDrawer: false,
        movieClubRenewalDrawer: false,
        voucherCheckoutDrawer: false,
    });

    const { nowShowing, comingSoon, isMoviesLoading } = useMovies();

    const [drawerContent, setDrawerContent] = useState(null);

    const openDrawer = (drawerName, content = null) => {
        setDrawerState((prev) => ({
            ...prev,
            [drawerName]: true,
        }));
        if (content) {
            setDrawerContent(content);
        }
    };


    const closeDrawer = (drawerName) => {
        setDrawerState((prev) => ({
            ...prev,
            [drawerName]: false,
        }));
        setDrawerContent(null);
    };

    const { children } = props;

    const clearLoginError = () => {
        setLoginError(null);
        setUnverifiedEmailToken(null);
    };

    const login = ({ username, password } = {}) => {
        setIsLoginSubmitting(true);
        axios
            .post(`${API_BASE_URL}/user/login`, { username, password })
            .then(function(response) {
                setIsLoginSubmitting(false);
                clearLoginError();
                setIsReadyToCheckout(true);
                setLoggedIn(true);

                setUserAuth((prevState) => ({
                    ...prevState,
                    user: response.data.data.user,
                    token: response.data.data.token,
                    isLoggedIn: true,
                    requireOtp: false,
                }));

                return window.location = "/";
            })
            .catch(({ response }) => {
                const errorMessage =
                    response?.status === 403 && response?.data?.message
                        ? response?.data?.message
                        : "Unknown error occurred";
                const user = response?.data?.user;

                setIsLoginSubmitting(false);
                setIsReadyToCheckout(false);
                setSuccessMessage(null);

                if (response?.status === 403 && response?.data?.is_blocked) {
                    window.location.href = "/unsuccessful-login";
                    return;
                }

                if (response?.data?.email_verified === false) {
                    setLoginError(errorMessage);
                    setUnverifiedEmailToken(user?.email_token ?? null);
                } else {
                    setLoginError(errorMessage);
                    setUnverifiedEmailToken(null);
                }

                setUserAuth((prevState) => ({
                    ...prevState,
                    user: null,
                    token: "",
                    requireOtp: false,
                }));
            });
    };

    const resendAccountCreation = (token) => {
        axios
            .get(`${API_BASE_URL}/user/account-creation-email/${token}`)
            .then((response) => {
                if (response?.data?.message) {
                    setLoginError(null);
                    setSuccessMessage(response.data.message);
                } else {
                    setSuccessMessage(null);
                    setLoginError("Error: No account creation mail was sent");
                    setErrorMessage("Error: No message received");
                }
            })
            .catch((error) => {
                setSuccessMessage(null);
                if (error.response && error.response.status === 500) {
                    setLoginError(error.response.data.error);
                    setErrorMessage(error.response.data.error);
                } else if (error.response && error.response.status === 400) { // if token is expired
                    setLoginError(error.response.data.message);
                    setErrorMessage(error.response.data.message);
                } else {
                    setLoginError("Unknown error occurred; please try again later.");
                    setErrorMessage("Unknown error occurred; please try again later.");
                }
            });
    };


    const createUserAccount = (data) => {
        setIsReadyToCheckout(false);
        axios
            .post(`${API_BASE_URL}/user/create-temp-account`, data)
            .then((response) => {
                const { user, token } = response.data.data;
                setIsSignupSubmitting(false);
                clearLoginError();
                setIsReadyToCheckout(true);
                setUserAuth((prevState) => ({
                    ...prevState,
                    user,
                    token,
                    isLoggedIn: true,
                    requireOtp: false,
                }));
                closeDrawer("fastAuthDrawer");
            })
            .catch((error) => {
                setIsSignupSubmitting(false);
                setIsReadyToCheckout(false);
                if (error.response.status === 500) {
                    setErrorMessages("Unknown error occurred");
                }
                if (error.response.status === 400) {
                    setErrorMessages(error.response.data.errors);
                } else {
                    setErrorMessages({
                        unknown_field: { unknown_error: "Unknown error occurred" },
                    });
                }
            });
    };

    const loginCheckout = ({ username, password } = {}) => {
        setIsLoginSubmitting(true);
        setIsReadyToCheckout(false);
        axios
            .post(`${API_BASE_URL}/user/login`, { username, password })
            .then(function(response) {
                setIsLoginSubmitting(false);
                clearLoginError();
                setIsReadyToCheckout(true);
                setLoggedIn(true);

                if (drawerState.loginSignUpDrawer) {
                    closeDrawer("loginSignUpDrawer");
                }

                return setUserAuth((prevState) => ({
                    ...prevState,
                    user: response.data.data.user,
                    token: response.data.data.token,
                    isLoggedIn: true,
                    requireOtp: false,
                }));
            })
            .catch(({ response }) => {
                const errorMessage =
                    response?.status === 403 && response?.data?.message
                        ? response?.data?.message
                        : "Unknown error occurred";
                setIsLoginSubmitting(false);
                setIsReadyToCheckout(false);

                if (response?.data?.is_blocked) {
                    window.location.href = "/unsuccessful-login";
                    return;
                }

                if (response?.data?.email_verified === false) {
                    setLoginError(errorMessage);
                    setUnverifiedEmailToken(response?.data?.user?.email_token ?? null);
                } else {
                    setLoginError(errorMessage);
                    setUnverifiedEmailToken(null);
                }
            });
    };

    const loginByToken = () => {
        axios
            .post(`${API_BASE_URL}/user/login-token`, {})
            .then((response) => {
                setIsReadyToCheckout(true);
                setUserAuth((prevState) => ({
                    ...prevState,
                    user: response.data.data.user,
                    token: response.data.data.token,
                    isLoggedIn: true,
                    requireOtp: false,
                }));
                if (response.data.data.gifting === "1") {
                    setMovieRedirect({ gifting: true });
                } else if (response.data.data.venue_name && response.data.data.movie_url) {
                    setMovieRedirect({
                        venueName: response.data.data.venue_name,
                        movieUrl: response.data.data.movie_url
                    });
                }
            })
            .catch(({ response }) => {
                const errorMessage =
                    response?.status === 401 && response?.data?.message
                        ? response?.data?.message
                        : "Unknown error occurred";
                setLoginError(errorMessage);
                setUserAuth((prevState) => ({
                    ...prevState,
                    user: null,
                    token: null,
                    isLoggedIn: false,
                    requireOtp: false,
                }));
            });
    };

    // Movie Club payment hand-off: Stripe groups get a client_secret and open the
    // embedded drawer; Eway groups keep the action_url redirect via the terms drawer.
    const handleMovieClubPayment = ({ action_url, client_secret, session_id }) => {
        if (client_secret) {
            setStripeSessionId(session_id ?? null);
            setStripeClientSecret(client_secret);
            openDrawer("stripeCheckoutDrawer");
            return;
        }

        setPendingActionUrl(action_url);
        openDrawer("termsDrawer");
    };

    const loginMovieClub = ({ username, password, membershipId } = {}) => {
        setIsLoginSubmitting(true);
        axios
            .post(`${API_BASE_URL}/movie-club/login`, { username, password, membershipId })
            .then(function (response) {
                setIsLoginSubmitting(false);
                clearLoginError();
                const { user, token } = response.data.data;
                setUserAuth((prevState) => ({
                    ...prevState,
                    user,
                    token,
                    isLoggedIn: true,
                    requireOtp: false,
                }));
                handleMovieClubPayment(response.data.data);
            })
            .catch(({ response }) => {
                const errorMessage =
                    (response?.status === 403 || response?.status === 400) && response?.data?.message
                        ? response?.data?.message
                        : "Unknown error occurred";
                setIsLoginSubmitting(false);
                setLoginError(errorMessage);
            });
    };



    const createMovieClubUserAccount = (data) => {
        axios
            .post(`${API_BASE_URL}/movie-club/signup`, data)
            .then((response) => {
                setIsLoginSubmitting(false);
                setIsSignupSubmitting(false);
                clearLoginError();
                const { user, token } = response.data.data;
                setUserAuth((prevState) => ({
                    ...prevState,
                    user,
                    token,
                    isLoggedIn: true,
                    requireOtp: false,
                }));
                handleMovieClubPayment(response.data.data);
            })
            .catch((error) => {
                setIsSignupSubmitting(false);
                if (error.response?.status === 500) {
                    setErrorMessages("Unknown error occurred");
                }
                if (error.response?.status === 400 || error.response?.status === 403) {
                    setErrorMessages(error.response.data.errors);
                } else {
                    setErrorMessages({
                        unknown_field: { unknown_error: "Unknown error occurred" },
                    });
                }
            });
    };


    const existingUserMovieClubSignup = (data) => {
        setIsSignupSubmitting(true);
        axios
            .post(`${API_BASE_URL}/movie-club/purchase`, data)
            .then((response) => {
                setIsSignupSubmitting(false);
                handleMovieClubPayment(response.data.data);
            })
            .catch((error) => {
                setIsSignupSubmitting(false);
                if (error.response?.status === 500) {
                    setErrorMessages("Unknown error occurred");
                }
                if (error.response?.status === 400 || error.response?.status === 403) {
                    setErrorMessages(error.response.data.errors);
                } else {
                    setErrorMessages({
                        unknown_field: { unknown_error: "Unknown error occurred" },
                    });
                }
            });
    };



    const logout = () => {
        axios
            .post(`${API_BASE_URL}/user/logout`)
            .then(function() {
                setUserAuth(() => ({
                    user: null,
                    token: null,
                    isLoggedIn: false,
                    requireOtp: false,
                }));
                setIsReadyToCheckout(false);
                window.location = "/";
            })
            .catch(() => {
                setUserAuth((prevState) => ({
                    ...prevState,
                    user: null,
                    token: null,
                    isLoggedIn: false,
                    requireOtp: false,
                }));
            });
    };

    const updateUser = (user) => {
        setUserAuth((prevState) => ({
            ...prevState,
            user,
        }));
    };



    const checkUserVerification = (token) => {
        setErrorMessage(null);
        setIsEmailTokenExpiredLoading(true);
        axios
            .get(`${API_BASE_URL}/user/get-user/${token}`)
            .then((response) => {
                const user = response?.data?.data?.user;
                const dateNow = new Date();

                if (user) {
                    if (user.sub_email_verified && user.verify_email_verified ) {
                        // User is already verified
                        setEmailToken(null);
                        setEmailTokenExpired(false);
                    } else if (user.sub_email_token ) {
                        if (new Date(user.sub_email_token_expires) >= dateNow) {
                            // User has email token that has NOT expired
                            setEmailToken(user?.sub_email_token);
                            setEmailTokenExpired(false);
                        } else {
                            // User has email token that IS expired
                            setEmailToken(user?.sub_email_token);
                            setEmailTokenExpired(true);
                        }
                    } else {
                        // User does not have a token
                        setEmailToken(null);
                        setEmailTokenExpired(true);
                    }
                } else {
                    // User was not returned
                    setEmailToken(null);
                    setEmailTokenExpired(true);
                }
                setIsEmailTokenExpiredLoading(false);
            })
            .catch((error) => {
                if (error.response && error.response.status === 500) {
                    setErrorMessage(error.response.data.error);
                } else {
                    setErrorMessage("Unknown error occurred; please try again later.");
                }
                setIsEmailTokenExpiredLoading(false);
            });
    };

    const addAccountMemberId = (data) => {
        setIsLoginSubmitting(true);
        axios
            .post(`${API_BASE_URL}/user/create-account-by-member-id`, data)
            .then(function(response) {
                setIsLoginSubmitting(false);

                // Check if OTP is required
                if (response.data.data && response.data.data.requireOtp) {
                    setLetsStartLoginError(null);
                    setSuccessMessage(response.data.message);
                    // Store email for OTP verification
                    setUserAuth((prevState) => ({
                        ...prevState,
                        pendingOtpEmail: response.data.data.email,
                        requireOtp: true,
                    }));
                } else {
                    // Regular login flow
                    setLetsStartLoginError(null);
                    setIsReadyToCheckout(true);
                    setLoggedIn(true);
                    setUserAuth((prevState) => ({
                        ...prevState,
                        user: response.data.data.user,
                        token: response.data.data.token,
                        isLoggedIn: true,
                        requireOtp: false,
                    }));
                    if (response.data.data.gifting === "1") {
                        window.location = "/gifting";
                    } else if (response.data.data.venue_name && response.data.data.venue_name !== "" && response.data.data.movie_url && response.data.data.movie_url !== "") {
                        window.location = `/movie/${  response.data.data.movie_url  }?venue_name=${  response.data.data.venue_name}`;
                    }
                    else
                    {
                        window.location = "/";
                    }
                }
            })
            .catch((error) => {
                const errorMessages = error?.response?.data?.message;
                setIsLoginSubmitting(false);
                setIsReadyToCheckout(false);
                setSuccessMessage(null);
                setLetsStartLoginError(errorMessages);
                setUserAuth((prevState) => ({
                    ...prevState,
                    user: null,
                    token: "",
                    isLoggedIn: false,
                    requireOtp: false,
                }));
            });
    };

    const verifyOtp = (otpCode) => {
        setIsLoginSubmitting(true);

        const data = {
            email_address: userAuth.pendingOtpEmail,
            otp_code: otpCode
        };

        axios
            .post(`${API_BASE_URL}/user/verify-otp`, data)
            .then(function(response) {
                setLetsStartLoginError(null);
                setIsReadyToCheckout(true);
                setLoggedIn(true);
                setIsLoginSubmitting(false);
                setUserAuth((prevState) => ({
                    ...prevState,
                    user: response.data.data.user,
                    token: response.data.data.token,
                    isLoggedIn: true,
                    requireOtp: false,
                    pendingOtpEmail: null
                }));

                if (response.data.data.gifting === "1") {
                    window.location = "/gifting";
                } else if (response.data.data.venue_name && response.data.data.venue_name !== "" && response.data.data.movie_url && response.data.data.movie_url !== "") {
                    window.location = `/movie/${  response.data.data.movie_url  }?venue_name=${  response.data.data.venue_name}`;
                }
                else
                {
                    window.location = "/";
                }
            })
            .catch((error) => {
                const errorMessages = error?.response?.data?.message;
                setIsLoginSubmitting(false);
                setIsReadyToCheckout(false);
                setSuccessMessage(null);
                setLetsStartLoginError(errorMessages);
                setUserAuth((prevState) => ({
                    ...prevState,
                    requireOtp: true,
                }));
            });
    };

    return (
        <AuthContext.Provider
            value={{
                errorMessage,
                isLoggedIn,
                isLoginSubmitting,
                login,
                loginCheckout,
                loginMovieClub,
                loginByToken,
                loginError,
                unverifiedEmailToken,
                clearLoginError,
                logout,
                loggedIn,
                processing,
                setErrorMessage,
                setLoginError,
                setProcessing,
                setSuccessMessage,
                successMessage,
                tempAccountLogin,
                setTempAccountLogin,
                token,
                updateUser,
                user,
                memberId,
                setMemberId,
                isReadytoCheckout,
                createUserAccount,
                createMovieClubUserAccount,
                existingUserMovieClubSignup,
                setIsReadyToCheckout,
                errorMessages,
                setErrorMessages,
                isSignupSubmitting,
                setIsSignupSubmitting,
                emailToken,
                setEmailToken,
                checkUserVerification,
                emailTokenExpired,
                isEmailTokenExpiredLoading,
                userAuth,
                setUserAuth,
                addAccountMemberId,
                nowShowing,
                comingSoon,
                setLetsStartLoginError,
                letsStartLoginError,
                resendAccountCreation,
                isMoviesLoading,
                drawerState,
                setDrawerState,
                openDrawer,
                closeDrawer,
                drawerContent,
                setDrawerContent,
                pendingActionUrl,
                setPendingActionUrl,
                stripeClientSecret,
                setStripeClientSecret,
                stripeSessionId,
                setStripeSessionId,
                verifyOtp,
                userCoords,
                setUserCoords,
                movieRedirect
            }}
        >
            {children}
        </AuthContext.Provider>
    );
};

export default AuthContextProvider;
